/** SQ-168: the choices the Review & request dialog offers, and what they produce. */

export type DeliverAs = 'zip' | 'single_pdf';
export type GroupBy = 'patient' | 'therapist';
export type DocumentHandling = 'subfolder' | 'merge';
export type OrderBy = 'date_discipline' | 'discipline_date';

export interface PackagingChoices {
  deliverAs: DeliverAs;
  groupBy: GroupBy;
  documentHandling: DocumentHandling;
  orderBy: OrderBy;
  /**
   * SQ-180/181: keep reform-sign's trailing audit pages on each document. Phrased as
   * *keep* rather than *remove* so a checked box means more content, like every other box
   * in the dialog. Default false — the trail roughly doubles the page count and is
   * internal provenance, not part of the clinical record.
   */
  includeAuditLog: boolean;
}

export const DEFAULT_PACKAGING: PackagingChoices = {
  deliverAs: 'zip',
  groupBy: 'patient',
  documentHandling: 'subfolder',
  orderBy: 'date_discipline',
  //  Not remembered between requests — the panels rebuild from this constant every time.
  includeAuditLog: false,
};

/**
 * SQ-184: no Physician. It grouped on `patient.primary_physician` — a patient-level
 * free-text column — rather than on the per-document `form_submissions.physician_id`,
 * so a By-resident pull produced exactly one folder and a cohort pull produced a tree
 * half named after physicians and half falling back to residents.
 */
export const GROUP_BY_LABELS: Record<GroupBy, string> = {
  patient: 'Patient',
  therapist: 'Therapist',
};

/**
 * Past this many pages the worker delivers the single PDF inside a ZIP. The real
 * decision is made there, with the files open — this is only so the dialog can say so
 * in advance. It is a warning, never a block (E2g).
 */
export const SINGLE_PDF_PAGE_LIMIT = 500;

/**
 * Rough, and labelled as such wherever it is shown. Until a facility has completed
 * jobs to average, a per-document estimate is the only thing available; SQ-165 now
 * records real sizes, so this can be replaced with a rolling average later.
 */
const BYTES_PER_DOCUMENT = 650 * 1024;
const PAGES_PER_DOCUMENT = 6;

export const estimateBytes = (documentCount: number) => documentCount * BYTES_PER_DOCUMENT;
export const estimatePages = (documentCount: number) => documentCount * PAGES_PER_DOCUMENT;

export const formatBytes = (bytes: number): string => {
  if (bytes <= 0) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (mb < 1024) return `${Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
};

/** The extension follows the choice, and so does the name the user sees (E2e). */
export const fileExtensionFor = (choices: PackagingChoices, documentCount: number): string => {
  if (choices.deliverAs !== 'single_pdf') return 'zip';
  return estimatePages(documentCount) > SINGLE_PDF_PAGE_LIMIT ? 'zip' : 'pdf';
};
